import { collection, doc, getDoc, getDocs, orderBy, query } from "firebase/firestore";
import { firestore } from "src/firebase/firebase";

// Collection names
const COLLECTIONS = {
  ACCOUNT_DELETION: "account_deletion_requests",
  FEEDBACK: "feedback",
  CONTACT_US: "contact_us"
};

// Helper function to normalize data from different collections
const normalizeMessage = (docData, docId, collectionType) => {
  const baseData = {
    id: docId,
    type: collectionType,
    createdAt: docData.createdAt?.toDate?.() || docData.timestamp?.toDate?.() || new Date(),
    ...docData
  };

  // Add type-specific formatting
  switch (collectionType) {
    case COLLECTIONS.ACCOUNT_DELETION:
      return {
        ...baseData,
        messageType: "Account Deletion Request",
        sender: docData.email || docData.userId || "Unknown",
        subject: "Account Deletion Request",
        status: docData.status || "pending"
      };
    case COLLECTIONS.FEEDBACK:
      return {
        ...baseData,
        messageType: "Feedback",
        sender: docData.email || docData.userName || docData.userId || "Unknown",
        subject: docData.subject || docData.title || "User Feedback",
        status: docData.status || docData.isRead ? "read" : "unread"
      };
    case COLLECTIONS.CONTACT_US:
      return {
        ...baseData,
        messageType: "Contact Us",
        sender: docData.email || docData.name || "Unknown",
        subject: docData.subject || docData.message?.substring(0, 50) || "Contact Message",
        status: docData.status || docData.isRead ? "read" : "unread"
      };
    default:
      return baseData;
  }
};

// Helper function to fetch from a single collection
const fetchFromCollection = async (collectionName, searchText) => {
  try {
    const collectionRef = collection(firestore, collectionName);
    let q = query(collectionRef, orderBy("createdAt", "desc"));

    // Add search filter if provided
    if (searchText?.trim()) {
      // Note: Firestore doesn't support full-text search natively
      // You may need to use a third-party solution like Algolia for better search
      // For now, we'll fetch all and filter client-side
    }

    const querySnapshot = await getDocs(q);
    const messages = [];

    querySnapshot.forEach((doc) => {
      const data = normalizeMessage(doc.data(), doc.id, collectionName);

      // Client-side search filtering
      if (searchText?.trim()) {
        const searchLower = searchText.toLowerCase();
        const matchesSearch =
          data.sender?.toLowerCase().includes(searchLower) ||
          data.subject?.toLowerCase().includes(searchLower) ||
          data.email?.toLowerCase().includes(searchLower) ||
          data.message?.toLowerCase().includes(searchLower);

        if (matchesSearch) {
          messages.push(data);
        }
      } else {
        messages.push(data);
      }
    });

    return messages;
  } catch (error) {
    console.error(`Error fetching from ${collectionName}:`, error);
    return [];
  }
};

export const messageService = {
  fetchMessages: async ({ pageSize, pageNumber, searchText, filterType }) => {
    try {
      // Fetch from specific collection only (no "all" option)
      const allMessages = await fetchFromCollection(filterType, searchText);

      // Sort by date (newest first)
      allMessages.sort((a, b) => b.createdAt - a.createdAt);

      // Calculate pagination
      const totalCount = allMessages.length;
      const startIndex = pageNumber * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedMessages = allMessages.slice(startIndex, endIndex);

      return {
        result: paginatedMessages,
        totalCount: totalCount,
        pageNumber: pageNumber,
        pageSize: pageSize
      };
    } catch (error) {
      console.error("Error fetching messages:", error);
      throw new Error(error?.message || "Unable to fetch messages");
    }
  },

  getMessageById: async (messageId, collectionType) => {
    try {
      if (!collectionType) {
        throw new Error("Collection type is required");
      }

      const docRef = doc(firestore, collectionType, messageId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return normalizeMessage(docSnap.data(), docSnap.id, collectionType);
      } else {
        throw new Error("Message not found");
      }
    } catch (error) {
      console.error("Error fetching message:", error);
      throw new Error(error?.message || "Unable to fetch message details");
    }
  },

  // Mark message as read
  markAsRead: async (messageId, collectionType) => {
    try {
      if (!collectionType) {
        throw new Error("Collection type is required");
      }

      const { updateDoc, doc } = await import("firebase/firestore");
      const docRef = doc(firestore, collectionType, messageId);
      await updateDoc(docRef, {
        isRead: true
      });
    } catch (error) {
      console.error("Error marking message as read:", error);
      throw new Error(error?.message || "Unable to mark message as read");
    }
  },

  // Mark message as unread
  markAsUnread: async (messageId, collectionType) => {
    try {
      if (!collectionType) {
        throw new Error("Collection type is required");
      }

      const { updateDoc, doc } = await import("firebase/firestore");
      const docRef = doc(firestore, collectionType, messageId);
      await updateDoc(docRef, {
        isRead: false
      });
    } catch (error) {
      console.error("Error marking message as unread:", error);
      throw new Error(error?.message || "Unable to mark message as unread");
    }
  },

  // Get message counts by type
  getMessageStats: async () => {
    try {
      const [accountDeletion, feedback, contactUs] = await Promise.all([
        getDocs(collection(firestore, COLLECTIONS.ACCOUNT_DELETION)),
        getDocs(collection(firestore, COLLECTIONS.FEEDBACK)),
        getDocs(collection(firestore, COLLECTIONS.CONTACT_US))
      ]);

      return {
        accountDeletionRequests: accountDeletion.size,
        feedback: feedback.size,
        contactUs: contactUs.size,
        total: accountDeletion.size + feedback.size + contactUs.size
      };
    } catch (error) {
      console.error("Error fetching message stats:", error);
      throw new Error(error?.message || "Unable to fetch message statistics");
    }
  },

  // Get unread message counts by type (where isRead === false)
  getUnreadCounts: async () => {
    try {
      const [accountDeletion, feedback, contactUs] = await Promise.all([
        getDocs(collection(firestore, COLLECTIONS.ACCOUNT_DELETION)),
        getDocs(collection(firestore, COLLECTIONS.FEEDBACK)),
        getDocs(collection(firestore, COLLECTIONS.CONTACT_US))
      ]);

      // Count documents where isRead is false or doesn't exist
      const countUnread = (querySnapshot) => {
        let count = 0;
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          // Check if isRead is false or undefined (treating undefined as unread)
          if (data.isRead === false || data.isRead === undefined) {
            count++;
          }
        });
        return count;
      };

      const accountDeletionUnread = countUnread(accountDeletion);
      const feedbackUnread = countUnread(feedback);
      const contactUsUnread = countUnread(contactUs);

      return {
        accountDeletionRequests: accountDeletionUnread,
        feedback: feedbackUnread,
        contactUs: contactUsUnread,
        total: accountDeletionUnread + feedbackUnread + contactUsUnread
      };
    } catch (error) {
      console.error("Error fetching unread counts:", error);
      // Return zeros if there's an error rather than throwing
      return {
        accountDeletionRequests: 0,
        feedback: 0,
        contactUs: 0,
        total: 0
      };
    }
  }
};
